import { useQuery } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { guardString } from "@msuite/utils/strings";
import type { BookingsReviewList } from "@msuite/utils/types";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";

/** The days the first page of the partner's records covers. */
const PAGE_SIZE = 7;

/**
 * The caller organisation's settlement records as a partner, first page only: `open` holds every
 * record it still owes hours on, `bookings` the booked or approved records of the page's days.
 * The route gates on `read Partner`, so the query waits for that rule.
 */
export function usePartnerBookingSubmissions() {
	/** Hooks */
	const { organization, citadel } = useAuth();

	/** Query */
	const queryKey = ["partner-bookings", "submissions", guardString(organization.id)];
	const query = useQuery({
		queryKey,
		queryFn: async () => {
			const response = await client.organizations[":orgId"].partnerships.bookings.submissions.$get({
				param: { orgId: guardString(organization.id) },
				query: { offset: "0", pageSize: String(PAGE_SIZE) },
			});
			const data = await responseHandler(response);
			return {
				open: data.open as BookingsReviewList,
				bookings: data.bookings as BookingsReviewList,
			};
		},
		enabled: citadel?.can("read", "Partner") === true,
	});

	/** Return */
	return {
		...query,
		open: query.data?.open ?? [],
		bookings: query.data?.bookings ?? [],
		queryKey,
	};
}
